import { useEffect } from "react";

/**
 * Accessibilità delle finestre modali (WCAG 2.1.1, 2.4.7, 4.1.2).
 *
 * I componenti `Modal` e `UrlPreview` del design system Maggioli vengono
 * renderizzati come semplici `div` privi di ruolo, nome accessibile e gestione
 * del focus, e la chiusura è affidata a un elemento `<i>` non focalizzabile.
 * Poiché il design system non espone alcuna prop utile a correggerli, questo
 * hook applica le integrazioni necessarie direttamente sui nodi renderizzati:
 *
 *  - ruolo `dialog` con `aria-modal` e nome accessibile;
 *  - pulsanti di chiusura operabili da tastiera e dotati di etichetta;
 *  - chiusura con il tasto Esc;
 *  - focus spostato nella finestra all'apertura, confinato al suo interno
 *    finché resta aperta e restituito all'elemento di partenza alla chiusura;
 *  - titolo sull'iframe del visualizzatore dei volumi.
 */

const DIALOG_SELECTOR = ".modal, .url-preview";
const CLOSE_SELECTOR = ".modal__close, .modal__close-inside, .url-preview__close, .url-preview__header .icon";
const VISIBLE_CLASSES = ["modal--visible", "url-preview--visible"];
const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "iframe",
    '[tabindex]:not([tabindex="-1"])',
    '[role="button"]',
].join(", ");

const DEFAULT_DIALOG_LABEL = "Finestra di dialogo";
const CLOSE_LABEL = "Chiudi";
const VIEWER_IFRAME_TITLE = "Visualizzatore del volume";

const isVisible = (dialog: HTMLElement): boolean => VISIBLE_CLASSES.some((c) => dialog.classList.contains(c));

// Gli elementi nascosti (ad esempio il contenuto chiuso di una fisarmonica)
// non devono entrare nel ciclo di tabulazione della finestra
const isFocusable = (el: HTMLElement): boolean => {
    const style = window.getComputedStyle(el);
    return style.display !== "none" && style.visibility !== "hidden";
};

const focusableInside = (dialog: HTMLElement): HTMLElement[] =>
    Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isFocusable);

// Nome accessibile: titolo della modale se presente, altrimenti la prima intestazione
const dialogLabel = (dialog: HTMLElement): string => {
    const title = dialog.querySelector(".modal__title, .url-preview__title");
    if (title !== null && title.textContent.trim() !== "") {
        return title.textContent.trim();
    }
    const heading = dialog.querySelector("h1, h2, h3, h4, h5, h6");
    if (heading !== null && heading.textContent.trim() !== "") {
        return heading.textContent.trim();
    }
    return DEFAULT_DIALOG_LABEL;
};

const enhanceCloseButtons = (dialog: HTMLElement): void => {
    dialog.querySelectorAll<HTMLElement>(CLOSE_SELECTOR).forEach((close) => {
        close.setAttribute("role", "button");
        close.setAttribute("tabindex", "0");
        close.setAttribute("aria-label", CLOSE_LABEL);
        if (close.dataset.a11yKeyboard === "true") {
            return;
        }
        close.dataset.a11yKeyboard = "true";
        close.addEventListener("keydown", (e: KeyboardEvent) => {
            if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar") {
                return;
            }
            e.preventDefault();
            close.click();
        });
    });
};

const enhance = (dialog: HTMLElement): void => {
    dialog.setAttribute("role", "dialog");
    dialog.setAttribute("aria-modal", "true");
    dialog.setAttribute("aria-label", dialogLabel(dialog));
    enhanceCloseButtons(dialog);

    const viewer = dialog.querySelector<HTMLIFrameElement>(".url-preview__iframe");
    if (viewer !== null && viewer.title === "") {
        viewer.title = VIEWER_IFRAME_TITLE;
    }
};

export const useModalAccessibility = (): void => {
    useEffect(() => {
        let openDialog: HTMLElement | null = null;
        let lastFocused: HTMLElement | null = null;

        const closeDialog = () => {
            const close = openDialog.querySelector<HTMLElement>(CLOSE_SELECTOR);
            close !== null && close.click();
        };

        const trapFocus = (e: KeyboardEvent) => {
            const focusables = focusableInside(openDialog);
            if (focusables.length === 0) {
                e.preventDefault();
                return;
            }
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement as HTMLElement;

            if (!openDialog.contains(active)) {
                e.preventDefault();
                first.focus();
                return;
            }
            if (e.shiftKey && active === first) {
                e.preventDefault();
                last.focus();
                return;
            }
            if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
            }
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (openDialog === null) {
                return;
            }
            if (e.key === "Escape" || e.key === "Esc") {
                e.preventDefault();
                closeDialog();
                return;
            }
            if (e.key === "Tab") {
                trapFocus(e);
            }
        };

        const open = (dialog: HTMLElement) => {
            if (openDialog === dialog) {
                return;
            }
            lastFocused = document.activeElement as HTMLElement;
            openDialog = dialog;
            enhance(dialog);
            // Il design system anima l'ingresso della finestra: si attende il
            // termine della transizione prima di spostare il focus.
            window.setTimeout(() => {
                if (openDialog !== dialog) {
                    return;
                }
                const focusables = focusableInside(dialog);
                if (focusables.length > 0) {
                    focusables[0].focus();
                    return;
                }
                dialog.setAttribute("tabindex", "-1");
                dialog.focus();
            }, 350);
        };

        const close = (dialog: HTMLElement) => {
            if (openDialog !== dialog) {
                return;
            }
            openDialog = null;
            if (lastFocused !== null && document.body.contains(lastFocused)) {
                lastFocused.focus();
            }
            lastFocused = null;
        };

        const sync = (dialog: HTMLElement) => {
            enhance(dialog);
            isVisible(dialog) ? open(dialog) : close(dialog);
        };

        const syncAll = () => {
            // La finestra aperta può essere stata smontata dal router
            if (openDialog !== null && !document.body.contains(openDialog)) {
                close(openDialog);
            }
            document.querySelectorAll<HTMLElement>(DIALOG_SELECTOR).forEach(sync);
        };

        // Le mutazioni del DOM sono frequenti: si accorpano in un solo passaggio per frame
        let scheduled: number | null = null;
        const scheduleSync = () => {
            if (scheduled !== null) {
                return;
            }
            scheduled = window.requestAnimationFrame(() => {
                scheduled = null;
                syncAll();
            });
        };

        const observer = new MutationObserver((mutations) => {
            let dirty = false;
            mutations.forEach((m) => {
                if (m.type === "attributes" && (m.target as HTMLElement).matches(DIALOG_SELECTOR)) {
                    dirty = true;
                    return;
                }
                if (m.type === "childList" && (m.addedNodes.length > 0 || m.removedNodes.length > 0)) {
                    dirty = true;
                }
            });
            dirty && scheduleSync();
        });

        observer.observe(document.body, {
            attributeFilter: ["class"],
            attributes: true,
            childList: true,
            subtree: true,
        });
        document.addEventListener("keydown", onKeyDown, true);
        syncAll();

        return () => {
            observer.disconnect();
            scheduled !== null && window.cancelAnimationFrame(scheduled);
            document.removeEventListener("keydown", onKeyDown, true);
        };
    }, []);
};
